import {getCountryCode, getLanguage} from '@smart/locale-service';
import {Button, Fieldset, Notification, Text} from '@smart/react-components';
import type {FormEventHandler} from 'react';
import {useEffect} from 'react';
import React, {useMemo, useRef, useState} from 'react';
import FormStyles from '../../common/form-styles.module.scss';
import {scrollToRef} from '../../common/helpers';
import type {
  CountryCode,
  LangCode,
  PhoneCode,
  ViewsFetchingToPostForm,
  availableCountries,
} from '../../common/types';
import {AppModelContext} from '../../components/app';
import {CountrySelect} from '../../components/country-select';
import Intercept from '../../components/easy-form/Intercept';
import Fields from '../../components/easy-form/fields';
import type {
  JSONObject,
  WrapperHandler,
} from '../../components/easy-form/types';
import {LegalLinks} from '../../components/legal-links';
import {MktLangSelect} from '../../components/mkt-lang-select';
import PhoneCodeSelect from '../../components/phone-code-select';
import PhoneField from '../../components/phone-field';
import {useAPI} from '../../hooks/use-api';
import {useLocale} from '../../hooks/use-locale';
import {useTracking} from '../../hooks/use-tracking';
import type {ITrackVIP} from '../../hooks/use-tracking/types';
import {useTranslations} from '../../hooks/use-translations';
import {useViews} from '../../hooks/use-views';
import {useSchemas} from '../../hooks/user-schemas';
import type {Success} from '../success';
import Styles from './reserve-my-seat-vip.module.scss';

export interface IReserveMySeatVIPState extends JSONObject {
  contact: {
    email: string;
    market: CountryCode;
    preferredLanguage: LangCode;
    address: string;
    city: string;
    message: string;
    phoneCode: PhoneCode;
    phoneNumber: string;
    postalCode: string;
  };
  personalInfo: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    salutation: string;
  };
  voucher: string;
  newsletter: boolean;
}

const viewName: ViewsFetchingToPostForm = `reserve-my-seat-vip`;

export default function ReserveMySeatVIP(): JSX.Element {
  const [hasErrors, setHasErrors] = useState(true);
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [formData, setFormData] = useState<IReserveMySeatVIPState>();
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string>();
  const [mkt, setMkt] = useState<CountryCode>();
  const {languageTag, marketId} = useLocale();
  const countryCode = getCountryCode(marketId);
  const language = getLanguage(languageTag);
  const translate = useTranslations();
  const apiFetch = useAPI(viewName, true);
  const {setView} = useViews();
  const [trackStep, successTrack] = useTracking<ITrackVIP>(
    `reservemyseat_vip`,
    `rms-vip`,
  );
  const cmsTranslations =
    React.useContext(AppModelContext).cms.formsFeatureAppReserveMySeatVipUi ||
    {};
  const apiTranslations =
    React.useContext(AppModelContext).cms.apiErrorMessageKeys || {};
  // UPDATE WHEN ERROR CODES ARE AVAILABLE FROM API
  // const cmsTranslationsErrors =
  //   React.useContext(AppModelContext).cms.formsFeatureAppValidation_ErrorsUi ||
  //   {};
  const formWrapperRef = useRef<HTMLDivElement>(null);
  // UPDATE WHEN ERROR CODES ARE AVAILABLE FROM API
  //const cfmError = cmsTranslationsErrors.validations_serverError?.toString();
  useEffect(() => trackStep(1, `form_start`), []);

  const onSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    if (hasErrors) {
      setSubmitAttempt(true);
      scrollToRef(formWrapperRef);
      return;
    }

    if (!formData) return;

    setLoading(true);
    setServerError(undefined);

    try {
      trackStep(1, `form_engagement`);
      await apiFetch(formData);
      successTrack(2, `success-rms-vip`);
      setView<typeof Success>(`success`, {
        successGreeting: cmsTranslations.reservemyseatvip_successPage_greeting,
        successTitle: cmsTranslations.reservemyseatvip_successPage_title,
      });
    } catch (error) {
      setServerError(error as string);
      setLoading(false);
      scrollToRef(formWrapperRef);
    }
  };

  const changeHandler: WrapperHandler = (state, errors) => {
    setHasErrors(!!errors);
    setFormData(state as IReserveMySeatVIPState);

    const selectedMKT = (state as any)?.contact?.market as
      | CountryCode
      | undefined;
    if (!!selectedMKT) {
      setMkt(selectedMKT);
    } else if (!!mkt) {
      setMkt(undefined);
    }
  };

  const generalSchemas = useSchemas();

  const schemas = useMemo(() => {
    const {
      singleLine,
      email,
      street,
      city,
      dateUntil,
      largeText,
      postalCode,
      firstAndLastNames,
    } = generalSchemas;
    return {
      firstName: firstAndLastNames(true),
      lastName: firstAndLastNames(true),

      address: street(true),
      email: email(true),
      dateOfBirth: dateUntil(new Date(), true),

      postalCode: postalCode(formData?.contact?.market, true),
      city: city(true),
      message: largeText(),

      voucher: singleLine(true, 10),
    };
  }, [generalSchemas, formData?.contact?.market]);

  const setValueFromAEM = () => {
    let apiErrorMessage = ``;
    let translatedString;

    const getStringFromAEM = apiTranslations.errorMessageKeys?.filter((e) => {
      return e?.includes(serverError as any);
    }, serverError);

    if (!getStringFromAEM) {
      apiErrorMessage = apiTranslations.generalFallbackErrorMessage as string;
    } else {
      translatedString = getStringFromAEM && getStringFromAEM[0]?.split(`:`);
      apiErrorMessage = translatedString && (translatedString[1] as any);
    }
    return apiErrorMessage;
  };

  return (
    <div className={FormStyles.wrapper} ref={formWrapperRef}>
      <Text className={FormStyles.upperText} variant="hl-100" as="p">
        {translate(`${viewName}.title`)}
      </Text>

      {serverError && (
        <Notification
          signal="error"
          className={FormStyles.serverError}
          onOpenChange={(open) => {
            if (!open) {
              setServerError(undefined);
            }
          }}
        >
          <Notification.Text>{setValueFromAEM()}</Notification.Text>
        </Notification>
      )}

      <form onSubmit={onSubmit} noValidate>
        <Fields.Wrapper
          translationPrefix={viewName}
          onUpdate={changeHandler}
          showErrors={submitAttempt}
          disabled={loading}
        >
          <Intercept
            set
            tag="personalInfo"
            label={cmsTranslations.reservemyseatvip_personalInformation}
          >
            <Fields.Text
              tag="firstName"
              required
              schema={schemas.firstName}
              label={
                cmsTranslations.reservemyseatvip_personalInformation_firstname
              }
            />
            <Fields.Text
              tag="lastName"
              required
              schema={schemas.lastName}
              label={
                cmsTranslations.reservemyseatvip_personalInformation_lastname
              }
            />
            <Fields.Text
              tag="dateOfBirth"
              required
              schema={schemas.dateOfBirth}
              props={{type: `date`}}
              label={
                cmsTranslations.reservemyseatvip_personalInformation_dateOfBirth
              }
            />
          </Intercept>

          <Intercept set tag="contact">
            <Fields.Text
              tag="email"
              required
              schema={schemas.email}
              label={cmsTranslations.reservemyseatvip_contact_email}
            />
            <Fields.Text
              tag="address"
              required
              schema={schemas.address}
              label={cmsTranslations.reservemyseatvip_contact_address}
            />
            <Fields.Text
              tag="postalCode"
              required
              schema={schemas.postalCode}
              label={cmsTranslations.reservemyseatvip_contact_postalCode}
            />
            <Fields.Text
              tag="city"
              required
              schema={schemas.city}
              label={cmsTranslations.reservemyseatvip_contact_city}
            />
            <CountrySelect
              required
              tag="market"
              initial={countryCode}
              label={cmsTranslations.reservemyseatvip_contact_market}
            />
            <div className={Styles.MktLangSelect}>
              <MktLangSelect
                initial={language}
                mkt={countryCode as (typeof availableCountries)[number]}
                required
                tag="preferredLanguage"
                label={
                  cmsTranslations.reservemyseatvip_contact_preferredLanguage
                }
              />
            </div>
            <div className={FormStyles.phoneFieldContainer}>
              <PhoneCodeSelect
                required
                mkt={mkt}
                tag="phoneCode"
                props={{className: FormStyles.phoneCode}}
                label={cmsTranslations.reservemyseatvip_contact_phoneCode}
              />
              <PhoneField
                required
                tag="phoneNumber"
                props={{className: FormStyles.phoneNumber}}
                label={cmsTranslations.reservemyseatvip_contact_phoneNumber}
              />
            </div>
            <Fields.Area
              tag="message"
              max={1024}
              schema={schemas.message}
              props={{className: Styles.message}}
              label={cmsTranslations.reservemyseatvip_message}
            />
          </Intercept>

          <Fieldset error="" style={{marginBottom: `var(--spacing-600)`}}>
            <Fields.Text
              tag="voucher"
              required
              schema={schemas.voucher}
              label={cmsTranslations.reservemyseatvip_voucher}
            />
          </Fieldset>

          <Fields.Checkbox
            label={cmsTranslations.reservemyseatvip_newsletter}
            props={{className: FormStyles.checkbox}}
            tag="newsletter"
          />

          <LegalLinks
            className={FormStyles.legal}
            legalText={cmsTranslations.legal}
          />
        </Fields.Wrapper>

        <Button
          type="submit"
          variant="primary"
          className={FormStyles.button}
          disabled={(submitAttempt && hasErrors) || loading}
        >
          {cmsTranslations.reservemyseatvip_submit || `Submit`}
        </Button>
      </form>
    </div>
  );
}
